import { z } from 'zod';

export type AiModelInfo = {
  id: string;
  name: string;
  provider: string;
};

export type CategorizedModels = {
  featured: AiModelInfo[];
  all: AiModelInfo[];
};

export type AiModelsResponse = {
  chat: CategorizedModels;
  image: CategorizedModels;
  agent: AiModelInfo[];
  defaults: {
    chatProvider: string;
    imageProvider: string;
    agentProvider: string;
  };
};

export type BrandProfile = {
  id: string;
  name: string;
  usp: string;
  uspKeywords: string[];
  audience: string;
  audienceKeywords: string[];
  language: string;
  languageKeywords: string[];
  story: string;
  storyKeywords: string[];
  otherInfo: string;
  otherInfoKeywords: string[];
  logoSrc?: string;
  brandValues: string;
  brandValuesKeywords: string[];
  coreValues: string;
  coreValuesKeywords: string[];
  toneOfVoice: string;
  toneOfVoiceKeywords: string[];
  wordsToEmbrace: string;
  wordsToEmbraceKeywords: string[];
  mainCompetitors: string;
  mainCompetitorsKeywords: string[];
  textSample: string;
  analyticsProfiles: {
    profileId: string;
    pageType: number;
  }[];
  website?: string;
  createdAt: string;
  updatedAt: string;
  topics: string[];
  contentFormat: string[];
  profileGroupId?: number | null;
  aiChatModel?: string | null;
  aiImageModel?: string | null;
  visualIdentity: string;
  visualIdentityKeywords: string[];
};

export type Creative = {
  id: string;
  brandProfileId: string;
  pageType: number;
  goal: string;
  text: string;
  createdAt: string;
  updatedAt: string;
};

export const WebCrawlResultSchema = z.object({
  url: z.string(),
  title: z.string(),
  description: z.string().nullable().optional(),
  image: z.string().nullable().optional(),
  logo: z.string().nullable().optional(),
  keywords: z.string().nullable().optional(),
  h1: z.array(z.string()),
  h2: z.array(z.string()),
  h3: z.array(z.string()),
  paragraphs: z.array(z.string()),
  socialTags: z.array(
    z.object({
      property: z.string(),
      content: z.string().nullable().optional(),
    }),
  ),
  bodyText: z.string(),
  bodyImages: z.array(z.string()),
  articleText: z.string(),
  articleImages: z.array(z.string()),
});

export type WebCrawlResult = z.infer<typeof WebCrawlResultSchema>;

export interface PageNode {
  id: string;
  _id: string;
  username: string | null;
  name: string;
  _type: number;
  fans: number | null;
  picture: string | null;
}

export interface PageEdge {
  node: PageNode;
}

export interface PageConnection {
  edges: PageEdge[];
  pageInfo: {
    hasNextPage: boolean;
    endCursor?: string;
  };
}

export type RawMetric = {
  name: string;
  value: number;
};

export type ProfileStatistics = {
  engagement: number;
  engagementRate: number;
  impressions: number;
  reach: number;
  reactions: number;
  comments: number;
  shares: number;
  clicks: number;
  videoViews: number;
  rawMetrics: RawMetric[];
};

export type ProfileReference = {
  id: string;
  type: number;
};

export type PostingFrequency = {
  overall: number;
  overallPerWeek: number;
  byProfile: Array<{
    profileId: string;
    pageType: number;
    postsPerDay: number;
    totalPosts: number;
  }>;
};

export type ContentTypePerformance = {
  type: string;
  engagementRate: number;
  count: number;
};

export type DayPerformance = {
  day: string;
  engagementRate: number;
  postCount: number;
};

export type HourPerformance = {
  hour: number;
  engagementRate: number;
  postCount: number;
};

export type PostPerformance = {
  postId: string;
  engagementRate: number;
  contentType: string;
  createdTime: string;
};

export type EngagementDistribution = {
  byPostType: Record<string, number>;
  byTimeOfDay: Record<string, number>;
  byDayOfWeek: Record<string, number>;
};

export type DetailedAnalytics = {
  postingFrequency: PostingFrequency;
  contentPerformance: {
    topContentTypes: ContentTypePerformance[];
    bestPerformingDays: DayPerformance[];
    bestPerformingHours: HourPerformance[];
    bestPerformingPosts: PostPerformance[];
  };
  engagementDistribution: EngagementDistribution;
  engagementPerPost: {
    average: number;
    median: number;
    posts: PostPerformance[];
  };
};

export type AccumulatedAnalytics = {
  engagement: number;
  engagementRate: number;
  impressions: number;
  reach: number;
  reactions: number;
  comments: number;
  shares: number;
  clicks: number;
  videoViews: number;
};

export type ProfileScore = {
  components: {};
  improvementTips: string[];
  overallScore: number;
  performanceAssessment: string;
};

export type BrandProfileAnalytics = {
  accumulated: AccumulatedAnalytics;
  individual: Array<{
    profile: ProfileReference;
    statistics: ProfileStatistics;
  }>;
  detailed: DetailedAnalytics;
  profileScore: ProfileScore;
};

export interface BrandProfileInsights {
  insights: {
    contentStrategy: Array<{
      recommendation: string;
      explanation: string;
    }>;
    postingSchedule: Array<{
      recommendation: string;
      explanation: string;
    }>;
    engagementTactics: Array<{
      recommendation: string;
      explanation: string;
    }>;
    growthOpportunities: Array<{
      recommendation: string;
      explanation: string;
    }>;
    competitivePositioning: Array<{
      recommendation: string;
      explanation: string;
    }>;
    keyTakeaways: string[];
  };
}

export type BrandHubTabType =
  | 'content'
  | 'performance'
  | 'overview'
  | 'content-ideas';

export interface PostIdea {
  id?: string;
  title: string;
  description: string;
  topics?: string[];
  contentFormat?: string[];
}

export interface ContentIdeasSuggestionsResponse {
  campaignId: number;
  chronological: PostIdea[][];
  meta?: {
    groundingType: 'online' | null;
    modelUsed: string | null;
    citations: Array<{ url: string; title: string | null }>;
  };
}

export type ExecutionStatus = {
  entityId?: string;
  brandId?: string;
  processType: string;
  isActive: boolean;
  traceId: string;
  entityName: string;
  startTime: string;
  duration: number;
  metadata: Record<string, any>;
};

export type ExecutionStatusResponse = ExecutionStatus;

export interface BrandInitializationRequest {
  url: string;
  planType: string;
  userAccountPublicId: string;
  analyticsProfiles?: { profileId: string; pageType: number }[];
  generateContentIdeas?: boolean;
  generateInitialPosts?: boolean;
  scrapeSocialMediaPosts?: boolean;
  importScrapedPosts?: boolean;
  numberOfInitialPosts?: number;
  ideaCount?: number;
}

export interface BrandInitializationResponse {
  initializationId: string;
  brandProfile: BrandProfile;
  crawlResult: {
    url: string;
    title: string;
    contentLength: number;
    logoFound: boolean;
  };
  campaign?: {
    id: string;
    name: string;
    description: string;
    contentIdeasCount: number;
  };
  initialPosts?: Array<{
    id: string;
    goal: string;
    text: string;
    createdAt: string;
  }>;
}

export type ImageResultStatus = 'ok' | 'failed';

export interface ImageResult {
  slotIndex: number;
  status: ImageResultStatus;
  image: string | null;
  error: string | null;
}

export interface PostIdeasCitation {
  url: string;
  title: string | null;
}

export type PostIdeasGroundingType = 'online' | null;

export interface OnboardingPostIdea {
  title: string;
  description: string;
  suggestedText?: string;
  topics?: string[];
  contentFormat?: string;
}

export interface PostIdeasContext {
  brandHint: string | null;
  sourceUrl: string | null;
  groundingType: PostIdeasGroundingType;
  citations: PostIdeasCitation[];
  modelUsed: string;
  groundingLatencyMs: number;
}

export interface PostIdeasResponse {
  ideas: OnboardingPostIdea[];
  context: PostIdeasContext;
}

export interface PostIdeasRequest {
  url?: string;
  prompt?: string;
  count?: number;
  language?: string;
  planType: string;
  /**
   * When true the backend routes the OpenRouter model id with an
   * `:online` suffix so the response includes fresh web citations.
   */
  grounded?: boolean;
}

export interface ContentIdeasMeta {
  groundingType: PostIdeasGroundingType;
  modelUsed: string | null;
  citations: PostIdeasCitation[];
}
